import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Selector de paciente con búsqueda por nombre (combobox).
 * Si allowCreatePending, ofrece usar el texto como paciente nuevo (pendiente de alta).
 */
export default function PacienteSearchSelect({
  pacientes = [],
  value,
  pendingNombre = '',
  onChange,
  onSelectPending,
  allowCreatePending = false,
  placeholder = 'Buscar paciente...',
  disabled = false,
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = useMemo(
    () => pacientes.find((p) => String(p.id) === String(value)),
    [pacientes, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? pacientes.filter((p) => (p.nombre || '').toLowerCase().includes(q))
      : pacientes;
    return list.slice(0, 40);
  }, [pacientes, query]);

  const canCreatePending = useMemo(() => {
    if (!allowCreatePending || !onSelectPending) return false;
    const q = query.trim();
    if (q.length < 2) return false;
    const exact = pacientes.some((p) => (p.nombre || '').trim().toLowerCase() === q.toLowerCase());
    return !exact;
  }, [allowCreatePending, onSelectPending, query, pacientes]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (!value && !pendingNombre) setQuery('');
  }, [value, pendingNombre]);

  const handleFocus = () => {
    if (disabled) return;
    setOpen(true);
    setQuery(selected?.nombre || pendingNombre || '');
  };

  const handleSelect = (paciente) => {
    onChange(String(paciente.id));
    setQuery('');
    setOpen(false);
  };

  const handlePending = () => {
    const nombre = query.trim();
    if (!nombre) return;
    onSelectPending?.(nombre);
    setOpen(false);
  };

  const inputValue = open ? query : (selected?.nombre || pendingNombre || '');

  return (
    <div className="citas-paciente-search" ref={wrapRef}>
      <div className="citas-paciente-search__control">
        <i className="bi bi-search citas-paciente-search__icon" aria-hidden />
        <input
          type="text"
          className="citas-nueva-input citas-paciente-search__input"
          placeholder={placeholder}
          value={inputValue}
          disabled={disabled}
          onFocus={handleFocus}
          onChange={(e) => {
            const next = e.target.value;
            setQuery(next);
            setOpen(true);
            if (!next.trim()) onChange('');
          }}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="citas-paciente-search-list"
        />
        {(value || pendingNombre) && !open ? (
          <button
            type="button"
            className="citas-paciente-search__clear"
            onClick={() => onChange('')}
            aria-label="Quitar paciente seleccionado"
          >
            <i className="bi bi-x-lg" aria-hidden />
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="citas-paciente-search__dropdown" id="citas-paciente-search-list" role="listbox">
          {filtered.length > 0 ? (
            <ul className="citas-paciente-search__list">
              {filtered.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={String(p.id) === String(value)}
                    className={[
                      'citas-paciente-search__option',
                      String(p.id) === String(value) ? 'is-selected' : '',
                    ].filter(Boolean).join(' ')}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(p)}
                  >
                    <span className="citas-paciente-search__option-name">{p.nombre}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="citas-paciente-search__empty">No hay pacientes registrados con ese nombre</p>
          )}
          {canCreatePending ? (
            <button
              type="button"
              className="citas-paciente-search__pending"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handlePending}
            >
              <i className="bi bi-person-plus" aria-hidden />
              Usar como paciente nuevo:{' '}
              <strong>{query.trim()}</strong>
              <span className="citas-paciente-search__pending-tag">Pendiente de registrar</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
